import React, { useEffect, useState } from 'react';
import { dealbin, files as filesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Sparkles, ShoppingBag, Timer, Loader2, DollarSign, Truck, MapPin, X, CheckCircle2 } from 'lucide-react';
import PayPalTrustBadge from '../components/PayPalTrustBadge';
import ClampWithMore from '../components/ClampWithMore';
import AppImage from '../components/AppImage';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export default function DealBin() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyFor, setBuyFor] = useState(null);   // item being purchased
  const [success, setSuccess] = useState(null); // { code, amount }

  const load = () => dealbin.list().then(setItems).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openBuy = (item) => { setSuccess(null); setBuyFor(item); };
  const closeBuy = () => { setBuyFor(null); };

  const totalUsd = (it) => (it?.dealBinPrice || 0) + (it?.deliveryMethod === 'shipping' ? (it?.deliveryFee || 0) : 0);

  return (
    <div className="space-y-6">
      <div className="pbb-card p-5 bg-gradient-to-br from-yellow-500/10 to-amber-500/5 border-yellow-500/30">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="text-yellow-400" size={20} />
          <h1 className="text-2xl font-bold text-yellow-300">Deal Bin</h1>
        </div>
        <p className="text-sm text-gray-300">Cash-only deals at <span className="text-yellow-300 font-semibold">80–90% off</span> declared value. Items land here after 30 days in the auction store with no bids. Every purchase earns you <span className="text-green-400 font-semibold">25 pts</span> and 100% goes to the two nonprofits.</p>
      </div>

      <PayPalTrustBadge />

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16} /> Loading Deal Bin…</div>
      ) : items.length === 0 ? (
        <div className="pbb-card border-2 border-dashed border-yellow-500/30 bg-yellow-500/5 p-10 text-center" data-testid="dealbin-empty-state">
          <ShoppingBag className="mx-auto text-yellow-400" size={30} />
          <h3 className="mt-4 text-xl font-bold text-yellow-300">Deal Bin opens with the auction store</h3>
          <p className="mt-2 text-sm text-gray-300 max-w-md mx-auto">Items land here at 80–90% off after 30 days without a bid. Register now — the first 100 members get priority notifications the second the Deal Bin activates.</p>
          <a href="/signup" data-testid="dealbin-empty-cta" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-semibold">Register Free</a>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => {
            const pctOff = it.estimatedValue ? Math.round((1 - (it.dealBinPrice || 0) / it.estimatedValue) * 100) : null;
            const isDonor = it.donorId === user?.id;
            return (
              <div key={it.id} data-testid={`dealbin-${it.id}`} className="pbb-card overflow-hidden border-yellow-500/20 hover:border-yellow-500/60 transition-colors flex flex-col h-full">
                <AppImage src={filesApi.urlFor(it.image)} alt={it.title} rounded="none" fallback={<ShoppingBag size={28}/>} />
                <div className="p-4 flex flex-col flex-1">
                  <div className="font-semibold text-sm pbb-card-title" title={it.title}>{it.title}</div>
                  <div className="mt-1 text-[11px] text-gray-500 inline-flex items-center gap-1">
                    {it.deliveryMethod === 'shipping' ? <><Truck size={10} /> Ships from donor</> : <><MapPin size={10} /> Local pickup</>}
                  </div>
                  <ClampWithMore text={it.description} lines={3} className="text-xs text-gray-400 mt-2" testId={`dealbin-desc-${it.id}`} />
                  <div className="mt-2 flex items-end justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500">Deal price</div>
                      <div className="text-xl font-bold text-yellow-300 flex items-center"><DollarSign size={16} />{(it.dealBinPrice || 0).toFixed(2)}</div>
                      {it.estimatedValue ? <div className="text-[11px] text-gray-500">Was ~${it.estimatedValue.toFixed(0)} · {pctOff}% off</div> : null}
                    </div>
                    {isDonor ? (
                      <div className="text-[10px] text-amber-400 text-right">Your item</div>
                    ) : (
                      <button data-testid={`dealbin-buy-${it.id}`} onClick={() => openBuy(it)}
                        className="px-3 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold inline-flex items-center gap-1">
                        <ShoppingBag size={12} /> Buy ${totalUsd(it).toFixed(2)}
                      </button>
                    )}
                  </div>
                  {it.deliveryMethod === 'shipping' && (it.deliveryFee || 0) > 0 && (
                    <div className="mt-2 text-[10px] text-gray-500">+ ${(it.deliveryFee || 0).toFixed(2)} shipping at checkout</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {buyFor && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4 overflow-y-auto py-6">
          <div className="w-full max-w-md bg-[#0f0f0f] border border-yellow-500/30 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
              <div className="font-semibold inline-flex items-center gap-2"><ShoppingBag size={16} className="text-yellow-400" /> Complete purchase</div>
              <button onClick={closeBuy} className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex gap-3">
                {buyFor.image && <img src={filesApi.urlFor(buyFor.image)} alt={buyFor.title} className="w-20 h-20 rounded-lg object-cover" />}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm">{buyFor.title}</div>
                  <div className="text-[11px] text-gray-400 mt-1 inline-flex items-center gap-1">
                    {buyFor.deliveryMethod === 'shipping' ? <><Truck size={10} /> Ships from donor</> : <><MapPin size={10} /> Local pickup</>}
                  </div>
                  {buyFor.deliveryDescription ? <div className="text-[11px] text-gray-500 mt-1 line-clamp-2">{buyFor.deliveryDescription}</div> : null}
                </div>
              </div>

              {/* Line items */}
              <div className="rounded-lg border border-[#1f1f1f] bg-black p-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-gray-400">Item price</span><span className="font-mono">${(buyFor.dealBinPrice || 0).toFixed(2)}</span></div>
                {buyFor.deliveryMethod === 'shipping' && (
                  <div className="flex justify-between"><span className="text-gray-400">Delivery fee</span><span className="font-mono">${(buyFor.deliveryFee || 0).toFixed(2)}</span></div>
                )}
                <div className="flex justify-between pt-1.5 border-t border-[#1f1f1f] text-white"><span className="font-semibold">Total</span><span className="font-mono font-semibold text-yellow-300">${totalUsd(buyFor).toFixed(2)}</span></div>
                <div className="text-[10px] text-green-400 pt-1">+25 pts credited to your account after payment</div>
              </div>

              {success ? (
                <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-4 text-center">
                  <CheckCircle2 className="mx-auto text-green-400" size={24} />
                  <div className="mt-2 font-semibold text-green-300">Purchase confirmed</div>
                  <div className="text-[11px] text-gray-400 mt-1">Pickup code — give this to the donor at handoff:</div>
                  <div className="mt-2 font-mono text-2xl tracking-widest text-white">{success.code}</div>
                  <button data-testid="dealbin-success-close" onClick={closeBuy} className="mt-4 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-black text-sm font-semibold">Done</button>
                </div>
              ) : PAYPAL_CLIENT_ID ? (
                <div data-testid="paypal-buttons-wrapper">
                  <PayPalScriptProvider options={{ 'client-id': PAYPAL_CLIENT_ID, currency: 'USD', intent: 'capture' }}>
                    <PayPalButtons
                      style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' }}
                      createOrder={async () => {
                        const res = await dealbin.createOrder(buyFor.id);
                        return res.orderId;
                      }}
                      onApprove={async (data) => {
                        try {
                          const res = await dealbin.capture(buyFor.id, data.orderID);
                          toast({ title: 'Purchase confirmed!', description: `+${res.buyerPointsAwarded} pts · Pickup code sent to your notifications.` });
                          setSuccess({ code: res.confirmationCode, amount: res.amountUsd });
                          await refresh();
                          await load();
                        } catch (err) {
                          toast({ title: 'Capture failed', description: err?.response?.data?.detail || 'Try again' });
                        }
                      }}
                      onError={(err) => {
                        toast({ title: 'Payment error', description: String(err?.message || err) });
                      }}
                    />
                  </PayPalScriptProvider>
                  <div className="text-center mt-2"><PayPalTrustBadge compact /></div>
                </div>
              ) : (
                <div className="text-xs text-red-400">PayPal is not configured. Please contact support.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
